import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Send, Sparkles, ChevronDown, ChevronUp, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AutoGrowTextarea } from "@/components/AutoGrowTextarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTest } from "@/context/TestContext";
import { useChatHalfHeight } from "@/hooks/use-chat-half-height";
import { useChatNarrowWidth } from "@/hooks/use-chat-narrow-width";
import { useChatExtended } from "@/hooks/use-chat-extended";
import { apiClient } from "@/lib/apiClient";
import { ChatMarkdown } from "@/components/ChatMarkdown";
type ChatContext = 'identify' | 'blueprint' | 'review-blueprint' | 'review-test' | 'directions' | 'general';

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

interface SmartAIChatProps {
  context?: ChatContext;
  isCollapsible?: boolean;
  defaultCollapsed?: boolean;
  className?: string;
  onCollapseChange?: (collapsed: boolean) => void;
  /** What the chevron in the header does.
   *  - "collapse" (default): hides the chat body, leaving just the
   *    header bar. The mobile sticky bottom panel uses this.
   *  - "half": keeps the body visible and instead fires onChevronToggle
   *    so a parent wrapper can shrink its own height (e.g. full-viewport
   *    ↔ half-viewport on the desktop blueprint sidebar).
   *  - "extend": keeps the body visible. The chevron toggles a shared
   *    "extended to viewport bottom" preference (useChatExtended) that
   *    review-test / edit-test reads to swap their sticky wrapper between
   *    compact and viewport-fill heights. */
  toggleMode?: "collapse" | "half" | "extend";
  onChevronToggle?: (isHalfHeight: boolean) => void;
  /** Show a "narrow width" toggle button next to the height chevron in the
   *  header. Defaults to true. When narrow is engaged, the component caps
   *  its own outer container at 75% of its parent and aligns right, so any
   *  page that hosts SmartAIChat picks up the behavior without per-page
   *  grid changes. Pass false for fixed-layout contexts (mobile sticky
   *  bottom bar) where shrinking the panel would look broken. */
  showWidthToggle?: boolean;
  focusedQuestion?: {
    number: number;
    stem: string;
    answers?: string[];
    correctAnswer?: number;
    level?: string;
  } | null;
  onClearFocus?: () => void;
  // The teacher's current blueprint, attached to the assistant's system
  // prompt on every message (the chat is stateless) so it always knows the
  // up-to-date topics/counts. Only passed from the Build Blueprint page.
  blueprintContext?: {
    testConfig?: { audience?: string; readingLevel?: string; testLengthType?: string; testLengthValue?: number; subject?: string };
    totalItems: number;
    topics: Array<{
      name: string;
      percentage: number;
      total: number;
      knowledge: number;
      understanding: number;
      applicationPlus: number;
      source?: string;
      addedManually?: boolean;
    }>;
  } | null;
  // Plain uploaded-materials text. Only attached to the request when the
  // "Suggest a topic" starter chip fires (one-shot), so normal messages
  // don't carry the content's tokens.
  materialsContext?: string;
}

export function SmartAIChat({
  context = 'general',
  isCollapsible = true,
  defaultCollapsed = false,
  className = "",
  onCollapseChange,
  toggleMode = "collapse",
  onChevronToggle,
  showWidthToggle = true,
  focusedQuestion,
  onClearFocus,
  blueprintContext,
  materialsContext,
}: SmartAIChatProps) {
  const { chatMessages: messages, setChatMessages: setMessages } = useTest();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  // Half-height preference is shared across every SmartAIChat instance
  // (any page using toggleMode="half"), so condensing on one page keeps
  // the assistant condensed when the user moves to the next.
  const [isHalfHeight, setIsHalfHeight] = useChatHalfHeight();
  const [isNarrowWidth, setIsNarrowWidth] = useChatNarrowWidth();
  const [isExtended, setIsExtended] = useChatExtended();
  // Whether the body of the chat is rendered. In "half" toggle mode it
  // stays mounted regardless of chevron clicks — the chevron only shrinks
  // the parent wrapper. In "collapse" mode the chevron hides it entirely.
  const showBody = toggleMode === "collapse" ? !isCollapsed : true;
  // Whether the chevron should point up (= "smaller" action available).
  // Up when the chat is currently in its larger state (half mode: full
  // viewport ↔ half; extend mode: extended ↔ compact). For the legacy
  // collapse mode, up means "currently collapsed → click to expand".
  const chevronUp =
    toggleMode === "half" ? isHalfHeight
      : toggleMode === "extend" ? isExtended
      : isCollapsed;
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Default to the bottom of the chat history whenever the assistant
  // appears in a new layout: a fresh mount (page navigation, mobile <->
  // desktop swap) or a size change (chevron toggling half <-> full
  // height). rAF gives the messages a tick to render so scrollHeight is
  // the final value before we apply scrollTop.
  useLayoutEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;
    const raf = requestAnimationFrame(() => {
      viewport.scrollTop = viewport.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [showBody, isHalfHeight, isExtended]);

  // Scroll to the bottom only right after the user sends their own message —
  // never when the assistant replies, so reading earlier messages isn't
  // disrupted by the view jumping down on each response.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.role !== "user") return;
    const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [messages]);

  // Sync collapsed state with prop changes
  useEffect(() => {
    setIsCollapsed(defaultCollapsed);
  }, [defaultCollapsed]);

  // When the user clicks "Chat with AI about the item", the parent sets
  // focusedQuestion. Bring the assistant to their attention: focus the input
  // and scroll it into view so it's clear the chat is now about that item.
  useEffect(() => {
    if (!focusedQuestion) return;
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedQuestion?.number]);

  const sendMessage = async (text: string, extra?: { materialsContext?: string }) => {
    if (!text.trim() || isTyping) return;

    const userMessage: Message = {
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      // Build messages array for API (exclude timestamps)
      const chatMessages = [...messages, userMessage]
        .filter(m => m.role === "user" || m.role === "assistant")
        .map(m => ({ role: m.role, content: m.content }));

      // Use apiClient so the bearer token is attached — /api/chat is behind
      // authMiddleware and a raw fetch (no auth header) always 401s, which is
      // why the assistant appeared "not hooked up".
      const response = await apiClient("/api/chat", {
        method: "POST",
        body: JSON.stringify({
          messages: chatMessages,
          context,
          focusedQuestion,
          blueprintContext,
          // Only present when the "Suggest a topic" chip passes it — keeps the
          // materials text off every other message.
          materialsContext: extra?.materialsContext,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      const assistantMessage: Message = {
        role: "assistant",
        content: data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      const errorMessage: Message = {
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = () => {
    // Bail if a reply is in flight so an Enter keypress doesn't clear the
    // user's in-progress draft (sendMessage also no-ops in this state).
    if (!inputValue.trim() || isTyping) return;
    const text = inputValue;
    setInputValue("");
    sendMessage(text);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`bg-card border-l-4 border-l-secondary shadow-lg rounded-lg overflow-hidden ${className.includes('h-full') ? 'flex flex-col' : ''} ${className}`}>
      {(() => {
        const toggleHeight = () => {
          if (!isCollapsible) return;
          if (toggleMode === "half") {
            const next = !isHalfHeight;
            setIsHalfHeight(next);
            onChevronToggle?.(next);
          } else if (toggleMode === "extend") {
            setIsExtended(!isExtended);
          } else {
            const next = !isCollapsed;
            setIsCollapsed(next);
            onCollapseChange?.(next);
          }
        };
        return (
          <div className="flex items-center justify-between p-4 bg-secondary/10">
            <div
              className={`flex items-center gap-2 flex-1 ${isCollapsible ? "cursor-pointer" : ""}`}
              onClick={toggleHeight}
              data-testid="button-toggle-chat"
            >
              <Sparkles className="w-5 h-5 text-secondary" />
              <h3 className="font-semibold text-foreground">AI Assistant</h3>
            </div>
            {isCollapsible && (
              <div className="flex items-center gap-1">
                {showWidthToggle && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsNarrowWidth(!isNarrowWidth);
                    }}
                    aria-label={isNarrowWidth ? "Widen assistant" : "Narrow assistant"}
                    data-testid="button-toggle-chat-width"
                  >
                    {isNarrowWidth ? (
                      <ChevronsLeft className="w-4 h-4" />
                    ) : (
                      <ChevronsRight className="w-4 h-4" />
                    )}
                  </Button>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleHeight();
                  }}
                  aria-label={chevronUp ? "Expand assistant" : "Condense assistant"}
                >
                  {chevronUp ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </Button>
              </div>
            )}
          </div>
        );
      })()}

      {showBody && (
        <div className={className.includes('h-full') ? 'flex flex-col flex-1 min-h-0' : ''}>
          <ScrollArea className={`${className.includes('h-full') ? 'flex-1 min-h-0' : 'h-64'} p-4`} ref={scrollAreaRef}>
            <div className="space-y-4">
              {messages.length === 0 && !isTyping && (
                <div className="py-6">
                  <p className="text-sm text-muted-foreground text-center">
                    Ask me anything about your test. I'll remember the conversation as you move between steps.
                  </p>
                </div>
              )}
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${message.role}-${index}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.role === "user"
                        ? "bg-primary/10 text-foreground"
                        : "bg-secondary/10 text-foreground"
                    }`}
                  >
                    {message.role === "assistant" ? (
                      <ChatMarkdown content={message.content} />
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    )}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start" data-testid="typing-indicator">
                  <div className="bg-secondary/10 rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" />
                      <div
                        className="w-2 h-2 bg-secondary rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      />
                      <div
                        className="w-2 h-2 bg-secondary rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="border-t bg-card">
            {focusedQuestion && (
              <div className="bg-muted/50 px-4 py-2 text-sm flex items-center justify-between">
                <span className="text-muted-foreground">
                  Focusing on question {focusedQuestion.number}
                </span>
                {onClearFocus && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onClearFocus}
                    className="h-6 px-2 text-xs"
                  >
                    Clear focus
                  </Button>
                )}
              </div>
            )}
            <div className="p-4">
              {context === 'blueprint' && (messages.length === 0 || materialsContext) && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {messages.length === 0 && (
                    <button
                      type="button"
                      onClick={() => sendMessage("What's a blueprint?")}
                      className="rounded-full border border-border px-3 py-1.5 text-sm hover:bg-muted transition-colors"
                      data-testid="chip-whats-a-blueprint"
                    >
                      What's a blueprint?
                    </button>
                  )}
                  {materialsContext && (
                    <button
                      type="button"
                      disabled={isTyping}
                      onClick={() => sendMessage(
                        "Based on my uploaded materials, what's a topic I could add to my blueprint? Suggest one or two that aren't already covered, and note where in my materials they come from.",
                        { materialsContext },
                      )}
                      className="rounded-full border border-border px-3 py-1.5 text-sm hover:bg-muted transition-colors disabled:opacity-50 disabled:pointer-events-none"
                      data-testid="chip-suggest-topic"
                    >
                      Suggest a topic to add
                    </button>
                  )}
                </div>
              )}
              <div className="flex gap-2 items-end">
                <AutoGrowTextarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ask me anything..."
                  maxLines={3}
                  className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  data-testid="input-chat-message"
                />
                <Button
                  onClick={handleSend}
                  size="icon"
                  disabled={!inputValue.trim() || isTyping}
                  data-testid="button-send-message"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}